import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.ts';
import { baht, parseBaht } from '../lib/format.ts';
import { promptPayPayload } from '../lib/promptpay.ts';
import { renderQrCanvas } from '../lib/slip.ts';
import { openDrawer, printBillSlip, whereLabel } from '../actions.ts';
import { useStore } from '../store.tsx';
import { Alert, AsyncButton, useToast } from '../ui.tsx';
import type { PayResult, PaymentMethod, SaleDetail } from '../lib/types.ts';

/**
 * กล่องชำระเงิน
 *
 * ★ ลอกจาก components/PaymentModal.js ของแอปเดิม ★
 *   · เด้งเป็นกล่องซ้อนบนหน้าขาย ไม่ใช่เปลี่ยนหน้า
 *   · หัวกล่อง "ชำระเงิน [ โต๊ะ X ]" พร้อมปุ่มปิดมุมขวา
 *   · ช่องทางเรียงลงมาทางซ้าย · ★ แยกบิล ★ อยู่ล่างสุด
 *   · ยอดชำระเงิน / รับเงิน / เงินทอน เรียงสามช่องบนสุดของฝั่งขวา
 *   · แป้น 100 · 500 · 1,000 · เต็มจำนวน แล้วต่อด้วยเลข ปุ่มยืนยันสูงสองแถว
 *   · ★ แตะช่องทางแล้วยอดเต็มเด้งใส่ช่องรับเงินให้เลย ★ กดต่อได้ทันทีหนึ่งครั้ง
 *
 * สองโหมดเหมือนเดิม
 *   ปกติ     เลือกช่องทางเดียว จ่ายทั้งบิล
 *   แยกบิล   สะสมการจ่ายหลายก้อนจนครบยอด
 *
 * ★ กติกาที่กล่องนี้ไม่มีสิทธิ์ตัดสินใจเอง ★
 * ยอดที่บันทึกและเงินทอนจริงมาจากเซิร์ฟเวอร์ · ลิ้นชักเปิดตามที่เซิร์ฟเวอร์บอก
 */

interface Tender {
  method: PaymentMethod;
  amountSatang: number;
  slipVerificationId: number | null;
}

export function PaymentModal({
  saleId,
  onClose,
  onPaid,
}: {
  saleId: number;
  onClose: () => void;
  onPaid: () => void;
}) {
  const store = useStore();
  const toast = useToast();

  const [sale, setSale] = useState<SaleDetail | null>(null);
  const [method, setMethod] = useState<PaymentMethod | null>(null);
  /** ★ โหมดแยกบิล ★ ของเดิมใช้รหัสช่องทาง '00' แทนสถานะนี้ */
  const [splitting, setSplitting] = useState(false);
  const [tenders, setTenders] = useState<Tender[]>([]);
  /** ★ แยกบิลแล้วยังเปิดแป้นค้าง ★ ของเดิมใช้ปุ่มลอย + เรียกแป้นกลับมา */
  const [padOpen, setPadOpen] = useState(false);
  const [entry, setEntry] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PayResult | null>(null);

  useEffect(() => {
    api
      .get<SaleDetail>(`/sales/${saleId}`)
      .then(setSale)
      .catch((err: Error) => setError(err.message));
  }, [saleId]);

  const total = sale?.totalSatang ?? 0;
  const entrySatang = entry === '' ? 0 : (parseBaht(entry) ?? 0);

  const taken = tenders.reduce((sum, t) => sum + t.amountSatang, 0);
  const remaining = Math.max(0, total - taken);

  /** เงินทอน — มีได้เฉพาะเงินสด ช่องทางอื่นเก็บตามยอดที่เรียกเก็บพอดี */
  const change =
    method?.kind === 'cash' && !splitting ? Math.max(0, entrySatang - remaining) : 0;

  const qrPayload = useMemo(() => {
    if (!store.branch.promptpayId || remaining <= 0) return null;
    if (!(method?.requiresSlip ?? false)) return null;
    try {
      // ★ QR ระบุยอดที่ยังต้องจ่ายไว้ในตัว ★ ลูกค้าไม่ต้องพิมพ์ตัวเลขเอง
      return promptPayPayload(store.branch.promptpayId, remaining);
    } catch {
      return null;
    }
  }, [store.branch.promptpayId, remaining, method]);

  /* ── แป้นตัวเลข ────────────────────────────────────────── */

  const press = (key: string) => {
    setEntry((prev) => {
      if (key === 'back') return prev.slice(0, -1);
      if (key === 'clear') return '';
      if (key === '.') return prev.includes('.') ? prev : (prev || '0') + '.';
      const [, fraction = ''] = prev.split('.');
      if (prev.includes('.') && fraction.length >= 2) return prev;
      if (key === '00') {
        if (prev.includes('.') && fraction.length >= 1) return prev + '0';
        return prev === '' || prev === '0' ? prev : prev + '00';
      }
      return prev === '0' ? key : prev + key;
    });
  };

  /* ── เพิ่มเงินหนึ่งก้อน ─────────────────────────────────── */

  const takeMoney = useCallback(
    async (m: PaymentMethod, amountSatang: number): Promise<Tender | null> => {
      let slipVerificationId: number | null = null;

      // ★ ช่องทางที่ต้องมีผลตรวจสลิป ปิดบิลไม่ได้ถ้าไม่มี ★
      if (m.requiresSlip) {
        const verification = await api.post<{ id: number }>('/slip-verifications', {
          branchId: store.branch.id,
          saleId,
          amountSatang,
          provider: 'manual',
          status: 'verified',
        });
        slipVerificationId = verification.id;
      }
      return { method: m, amountSatang, slipVerificationId };
    },
    [store.branch.id, saleId],
  );

  /** ปิดบิลด้วยรายการที่ระบุ */
  const settle = useCallback(
    async (rows: Tender[]) => {
      const paid = await api.post<PayResult>(`/sales/${saleId}/pay`, {
        tenders: rows.map((t) => ({
          paymentMethodId: t.method.id,
          amountSatang: t.amountSatang,
          slipVerificationId: t.slipVerificationId,
        })),
      });
      setResult(paid);

      // ★ ลิ้นชักเปิดตามที่เซิร์ฟเวอร์บอกเท่านั้น ★
      const drawerProblem = await openDrawer(store, paid.shouldOpenDrawer, paid.drawerReason);
      if (drawerProblem) toast(drawerProblem, 'warn');

      const fresh = await api.get<SaleDetail>(`/sales/${saleId}`);
      const printProblem = await printBillSlip(store, fresh, false);
      if (printProblem) toast(`ใบเสร็จพิมพ์ไม่ออก — ${printProblem}`, 'crit');
    },
    [saleId, store, toast],
  );

  /** ปุ่มยืนยันในโหมดปกติ — จ่ายช่องทางเดียวจบทั้งบิล */
  const confirmSingle = async () => {
    setError(null);
    if (!method) { setError('เลือกช่องทางการชำระก่อน'); return; }
    if (entrySatang <= 0) { setError('ใส่จำนวนเงินก่อน'); return; }
    if (method.kind !== 'cash' && entrySatang !== remaining) {
      setError(`${method.name} ต้องรับพอดี ${baht(remaining)} บาท`);
      return;
    }
    if (entrySatang < remaining) {
      setError(`ยังขาดอีก ${baht(remaining - entrySatang)} บาท`);
      return;
    }

    try {
      const tender = await takeMoney(method, method.kind === 'cash' ? entrySatang : remaining);
      if (tender) await settle([...tenders, tender]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ปิดบิลไม่สำเร็จ');
    }
  };

  /** ปุ่มเพิ่มในโหมดแยกบิล */
  const addSplit = async () => {
    setError(null);
    if (!method) { setError('เลือกช่องทางการชำระก่อน'); return; }
    if (entrySatang <= 0) { setError('ใส่จำนวนเงินก่อน'); return; }
    if (entrySatang > remaining) {
      setError(`ใส่ได้ไม่เกินยอดที่เหลือ ${baht(remaining)} บาท`);
      return;
    }
    try {
      const tender = await takeMoney(method, entrySatang);
      if (tender) {
        setTenders((list) => [...list, tender]);
        setEntry('');
        setPadOpen(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'เพิ่มรายการไม่สำเร็จ');
    }
  };

  const finishSplit = async () => {
    setError(null);
    if (remaining > 0) { setError(`ยังขาดอีก ${baht(remaining)} บาท`); return; }
    try {
      await settle(tenders);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ปิดบิลไม่สำเร็จ');
    }
  };

  /* ── หน้าจอ ─────────────────────────────────────────────── */

  if (!sale) {
    return (
      <div className="overlay">
        <div className="paydlg" style={{ height: 160, width: 320 }}>
          <div className="center-screen"><div className="spinner" /></div>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="overlay">
        <div className="paydlg" style={{ height: 'auto', width: 'min(420px, 94vw)' }}>
          <div className="paydlg-head"><span className="grow">ปิดบิลแล้ว</span></div>
          <div style={{ padding: 20 }}>
            <p className="lede">{result.receiptNo}</p>
            {result.changeSatang > 0 && (
              <div className="alert ok" style={{ fontSize: '1.7rem', fontWeight: 700 }}>
                เงินทอน {baht(result.changeSatang)} บาท
              </div>
            )}
            <div className="total-row grand">
              <span>ยอดบิล</span>
              <span className="v">{baht(result.totalSatang)}</span>
            </div>
            <div className="action-row" style={{ marginTop: 18 }}>
              <AsyncButton
                className="btn ghost"
                onClick={async () => {
                  const fresh = await api.get<SaleDetail>(`/sales/${saleId}`);
                  const problem = await printBillSlip(store, fresh, false);
                  toast(problem ?? 'พิมพ์ใบเสร็จซ้ำแล้ว', problem ? 'crit' : 'ok');
                }}
              >
                พิมพ์ใบเสร็จอีกใบ
              </AsyncButton>
              <button className="btn big" onClick={onPaid}>เสร็จ</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const quick: Array<[string, number]> = [
    ['100', 10000],
    ['500', 50000],
    ['1,000', 100000],
  ];

  return (
    <div
      className="overlay"
      onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="paydlg" role="dialog" aria-label="ชำระเงิน">
        <div className="paydlg-head">
          <span className="grow">ชำระเงิน [ {whereLabel(store, sale)} ]</span>
          <button onClick={onClose} title="ปิด">✕</button>
        </div>

        <div className="paydlg-body">
          {/* ── ช่องทางการชำระ ── */}
          <div className="paydlg-rail">
            {store.paymentMethods.map((m) => (
              <button
                key={m.id}
                className="method"
                aria-pressed={method?.id === m.id}
                onClick={() => {
                  setMethod(m);
                  // ★ แตะช่องทางแล้วยอดที่เหลือเด้งใส่ช่องรับเงินให้เลย ★
                  //   พฤติกรรมเดิม — กดต่อได้ทันทีหนึ่งครั้งโดยไม่ต้องพิมพ์เลข
                  setEntry(String(remaining / 100));
                  setError(null);
                }}
              >
                {m.name}
              </button>
            ))}

            <span className="spacer" />

            {/* ระหว่างแยกบิลแล้วเปิดแป้นอยู่ ★ ทางกลับไปดูของที่รับมาแล้ว */}
            {splitting && tenders.length > 0 && padOpen && (
              <button className="paydlg-back" onClick={() => setPadOpen(false)}>
                ← รายการที่รับแล้ว ({tenders.length})
              </button>
            )}

            <button
              className="split"
              aria-pressed={splitting}
              onClick={() => {
                // ของเดิมกดแยกบิลแล้วล้างช่องทางและยอดที่กรอกไว้ทิ้ง
                setSplitting((s) => !s);
                setMethod(null);
                setEntry('');
                setTenders([]);
                setPadOpen(false);
                setError(null);
              }}
            >
              {splitting ? 'เลิกแยกบิล' : 'แยกบิล'}
            </button>
          </div>

          {/* ── ยอดและแป้น ── */}
          <div className="paydlg-main">
            <div className="paydlg-sum">
              <div>
                <div className="k">ยอดชำระเงิน</div>
                <div className="v">{baht(remaining)}</div>
              </div>
              <div>
                <div className="k">รับเงิน</div>
                <div className="v box">{entry === '' ? '0.00' : baht(entrySatang)}</div>
              </div>
              <div>
                <div className="k">เงินทอน</div>
                <div className="v change">{change > 0 ? baht(change) : '–'}</div>
              </div>
            </div>

            {error && <Alert>{error}</Alert>}

            <div className="paydlg-work">
              {splitting && tenders.length > 0 && !padOpen ? (
                <div className="paydlg-split">
                  <div className="list">
                    {tenders.map((t, index) => (
                      <div key={index} className="row">
                        <span className="n">
                          {t.method.name}
                          {t.slipVerificationId !== null && (
                            <span style={{ color: 'var(--ok)', fontSize: '.8rem' }}> · ยืนยันแล้ว</span>
                          )}
                        </span>
                        <span className="a">{baht(t.amountSatang)}</span>
                        <button onClick={() => setTenders((l) => l.filter((_, i) => i !== index))}>
                          ลบ
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    className="paydlg-fab"
                    onClick={() => setPadOpen(true)}
                    title="เพิ่มรายการรับเงิน"
                  >
                    +
                  </button>
                  <div className="foot">
                    <span className="info">
                      {remaining > 0
                        ? <>ยังขาดอีก <b>{baht(remaining)}</b> บาท</>
                        : <b style={{ color: 'var(--rail-active)' }}>ครบแล้ว</b>}
                    </span>
                    <AsyncButton
                      className="btn big"
                      onClick={finishSplit}
                      disabled={remaining > 0}
                    >
                      ปิดบิล
                    </AsyncButton>
                  </div>
                </div>
              ) : (
                <>
                  <div className="paydlg-pad">
                    {quick.map(([label, satang]) => (
                      <button
                        key={label}
                        className="quick"
                        onClick={() => setEntry(String(satang / 100))}
                      >
                        {label}
                      </button>
                    ))}
                    <button className="quick" onClick={() => setEntry(String(remaining / 100))}>
                      เต็มจำนวน
                    </button>

                    <button onClick={() => press('7')}>7</button>
                    <button onClick={() => press('8')}>8</button>
                    <button onClick={() => press('9')}>9</button>
                    <button onClick={() => press('back')} title="ลบทีละตัว">←</button>

                    <button onClick={() => press('4')}>4</button>
                    <button onClick={() => press('5')}>5</button>
                    <button onClick={() => press('6')}>6</button>
                    <button onClick={() => press('clear')} title="ล้าง">✕</button>

                    <button onClick={() => press('1')}>1</button>
                    <button onClick={() => press('2')}>2</button>
                    <button onClick={() => press('3')}>3</button>
                    <AsyncButton
                      className="confirm"
                      onClick={splitting ? addSplit : confirmSingle}
                      disabled={!method || entrySatang <= 0}
                    >
                      {splitting ? 'เพิ่ม' : 'ยืนยัน'}
                    </AsyncButton>

                    <button onClick={() => press('0')}>0</button>
                    <button onClick={() => press('00')}>00</button>
                    <button onClick={() => press('.')}>.</button>
                  </div>

                  {qrPayload && (
                    <div className="paydlg-qr">
                      <QrImage payload={qrPayload} />
                      <span className="note">
                        พร้อมเพย์ · ยอดขึ้นเองอัตโนมัติ<br />ลูกค้าไม่ต้องพิมพ์ตัวเลข
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** QR เรนเดอร์ใหญ่ไว้ก่อน แล้วให้ CSS ย่อลง — ขอบยังคมเพราะไม่เกลี่ยสี */
function QrImage({ payload }: { payload: string }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    try {
      setUrl(renderQrCanvas(payload, 420).toDataURL('image/png'));
    } catch {
      setUrl(null);
    }
  }, [payload]);

  if (!url) return <span className="note">สร้าง QR ไม่ได้ — ตรวจเลขพร้อมเพย์ของสาขา</span>;
  return <img src={url} alt="QR พร้อมเพย์" />;
}
